import { useLanguage } from '../../contexts/LanguageContext';
import { Vote, TrendingUp } from 'lucide-react';

/**
 * Live Election Results panel - shown above the hero when admin enables it.
 * Data is manually maintained from the admin panel.
 *
 * config shape:
 * {
 *   enabled: boolean,
 *   title: string,
 *   title_kn: string,
 *   subtitle: string,
 *   subtitle_kn: string,
 *   total_seats: number,         // optional, used for majority marker
 *   parties: [
 *     { id, name, name_kn, logo, color, seats, votes_pct }
 *   ]
 * }
 */
export default function ElectionPanel({ config }) {
  const { isKannada } = useLanguage();
  if (!config || config.enabled !== true) return null;
  const parties = Array.isArray(config.parties) ? config.parties.filter(p => p && (p.name || p.name_kn)) : [];
  if (!parties.length) return null;

  const title = isKannada ? (config.title_kn || config.title || 'ಚುನಾವಣಾ ಫಲಿತಾಂಶ') : (config.title || 'Live Election Results');
  const subtitle = isKannada ? (config.subtitle_kn || config.subtitle || '') : (config.subtitle || '');
  const totalSeats = Number(config.total_seats) || parties.reduce((s, p) => s + (Number(p.seats) || 0), 0);
  const sorted = parties.slice().sort((a, b) => (Number(b.seats) || 0) - (Number(a.seats) || 0));
  const leader = sorted[0];

  return (
    <div className="bg-gradient-to-r from-[#0c1f3a] via-[#13294b] to-[#0c1f3a] text-white" data-testid="election-panel">
      <div className="max-w-[1400px] mx-auto px-4 py-3">
        <div className="flex items-start justify-between gap-4 mb-2 flex-wrap">
          <div className="flex items-center gap-2.5">
            <span className="w-9 h-9 rounded-full bg-white/15 flex items-center justify-center"><Vote className="w-5 h-5" /></span>
            <div>
              <h3 className="text-[14px] font-black uppercase tracking-wide flex items-center gap-2">
                <span className="px-2 py-0.5 rounded bg-red-600 text-[10px] font-black animate-pulse">LIVE</span>
                {title}
              </h3>
              {subtitle && <p className="text-[11px] text-white/70 mt-0.5">{subtitle}</p>}
            </div>
          </div>
          {leader && (
            <div className="text-right">
              <p className="text-[10px] uppercase tracking-widest text-white/60">{isKannada ? 'ಮುಂಚೂಣಿಯಲ್ಲಿ' : 'Leading'}</p>
              <p className="text-[15px] font-black flex items-center gap-1.5 justify-end" style={{ color: leader.color || '#ffd54f' }}>
                <TrendingUp className="w-4 h-4" />
                {isKannada ? (leader.name_kn || leader.name) : leader.name} · {leader.seats || 0}
              </p>
            </div>
          )}
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2.5">
          {sorted.map((p) => {
            const pct = totalSeats > 0 ? Math.min(100, Math.round(((Number(p.seats) || 0) / totalSeats) * 100)) : 0;
            const color = p.color || '#ffd54f';
            return (
              <div
                key={p.id || p.name}
                className="bg-white/10 hover:bg-white/15 transition-colors rounded-lg px-3 py-2 flex items-center gap-2.5"
                data-testid={`election-party-${p.id || p.name}`}
              >
                {p.logo ? (
                  <img src={p.logo} alt="" className="w-9 h-9 rounded-full object-cover bg-white p-0.5 shrink-0" />
                ) : (
                  <span className="w-9 h-9 rounded-full shrink-0 flex items-center justify-center font-black text-[13px]" style={{ backgroundColor: color, color: '#0c1f3a' }}>
                    {(p.name_kn || p.name || '?').charAt(0)}
                  </span>
                )}
                <div className="min-w-0 flex-1">
                  <div className="flex items-baseline gap-1.5">
                    <span className="text-[12px] font-bold truncate font-kannada">{isKannada ? (p.name_kn || p.name) : p.name}</span>
                  </div>
                  <div className="flex items-baseline gap-2">
                    <span className="text-[16px] font-black leading-none" style={{ color }}>{p.seats || 0}</span>
                    {p.votes_pct != null && p.votes_pct !== '' && (
                      <span className="text-[10px] text-white/60">{p.votes_pct}%</span>
                    )}
                  </div>
                  <div className="h-1 bg-white/15 rounded-full mt-1 overflow-hidden">
                    <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, backgroundColor: color }} />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
